// Navbar.tsx
import { useState, useEffect } from "react";
import { Logo } from "./Logo";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { CafeOwnerForm } from "./CafeOwnerForm";

export function Navbar() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("Product");
  const [isHidden, setIsHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous: any = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setIsHidden(true);
    } else {
      setIsHidden(false);
    }
  });

  const navLinks = [
    { name: "Product", href: "#product" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Features", href: "#features" },
    { name: "Pricing", href: "#pricing" },
    { name: "Contact", href: "#contact" },
  ];

  const handleNavClick = (link: string, href: string) => {
    setActiveLink(link);
    setIsMobileMenuOpen(false);

    // Remove the # from href to get the id
    const id = href.replace("#", "");
    const element = document.getElementById(id);
    
    if (element) {
      // Get navbar height for offset
      const navbarHeight = 80; // Adjust this value based on your navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Update active link based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = navLinks.map(link => ({
        name: link.name,
        element: document.getElementById(link.href.replace("#", ""))
      }));

      // Find which section is currently in view
      const current = sections.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (current) {
        setActiveLink(current.name);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.nav
        variants={{
          visible: { y: 0 },
          hidden: { y: "-100%" },
        }}
        animate={isHidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-md border-b border-neutral-200"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-700">
            {navLinks.map((link, index) => (
              <motion.li
                key={link.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                onClick={() => handleNavClick(link.name, link.href)}
                className="relative cursor-pointer transition-colors duration-200"
              >
                <span
                  className={`${
                    activeLink === link.name ? "text-black font-semibold" : ""
                  }`}
                >
                  {link.name}
                </span>

                {/* Animated underline */}
                {activeLink === link.name && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-black"
                    transition={{
                      type: "spring",
                      stiffness: 350,
                      damping: 30,
                    }}
                  />
                )}

                {/* Hover effect */}
                <motion.div
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-neutral-300"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: activeLink !== link.name ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                />
              </motion.li>
            ))}
          </ul>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsFormOpen(true)}
            className="hidden md:block px-6 py-2 bg-black text-white rounded-full hover:bg-neutral-800 transition-colors duration-200 text-sm font-medium"
          >
            Get Started
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: isMobileMenuOpen ? "auto" : 0,
            opacity: isMobileMenuOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="md:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-neutral-200"
        >
          <ul className="flex flex-col p-4 gap-2">
            {navLinks.map((link, index) => (
              <motion.li
                key={link.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  x: isMobileMenuOpen ? 0 : -20,
                }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleNavClick(link.name, link.href)}
                className={`text-neutral-700 hover:text-black cursor-pointer py-3 px-4 rounded-lg hover:bg-neutral-100 transition-colors ${
                  activeLink === link.name
                    ? "bg-neutral-100 text-black font-semibold"
                    : ""
                }`}
              >
                {link.name}
              </motion.li>
            ))}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: isMobileMenuOpen ? 1 : 0,
                y: isMobileMenuOpen ? 0 : 20,
              }}
              transition={{ delay: 0.5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsFormOpen(true);
              }}
              className="mt-4 px-6 py-3 bg-black text-white rounded-full hover:bg-neutral-800 transition-colors duration-200 text-sm font-medium"
            >
              Register Your Cafe
            </motion.button>
          </ul>
        </motion.div>
      </motion.nav>
      <CafeOwnerForm isOpen={isFormOpen} setIsOpen={setIsFormOpen} />
    </>
  );
}
